import { NewsletterSection } from '@/components/home/NewsletterSection';
import { ServiceTargetSection, TargetAudience } from '@/components/prestations/ServiceTargetSection';
import { UseCaseCard } from '@/components/prestations/UseCaseCard';
import {
  Lightbulb,
  Mic,
  Radio,
  Users,
  Video,
  Mail,
  Calendar,
  CheckCircle,
  CalendarDays,
  Newspaper,
  Building2,
  MapPin,
  Target,
  GraduationCap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SEOHead } from '@/components/SEO/SEOHead';

export function InterventionsPage() {
  const { t, i18n } = useTranslation('interventions');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';
  const targetAudiences: TargetAudience[] = [
    {
      icon: CalendarDays,
      title: "Organisateurs d'événements",
      description: "Conférences, salons agricoles, assemblées générales de coopératives : interventions pédagogiques et tables rondes sur le numérique agricole",
      badge: "50+ événements",
    },
    {
      icon: Newspaper,
      title: "Médias agricoles",
      description: "Interviews, tribunes et chroniques sur les enjeux du numérique en agriculture : analyses indépendantes, vulgarisation technique et décryptage des tendances",
      badge: "30+ interventions médias",
    },
    {
      icon: Building2,
      title: "Entreprises Agri-tech",
      description: "Webinaires clients, présentations stratégiques et animation de débats sur vos événements professionnels pour valoriser votre expertise",
      badge: "20+ entreprises partenaires",
    },
  ];

  const useCases = [
    {
      client: 'L\'empreinte environnementale du numérique agricole',
      context: 'Chaire AgroTIC - 10 ans - Bordeaux (Décembre 2024)',
      challenge: 'Présenter l\'état de l\'art scientifique sur l\'impact environnemental des outils numériques en agriculture lors d\'un événement anniversaire majeur',
      solution: 'Intervention de 45 minutes synthétisant les travaux de recherche récents (thèse Clémence Huck, travaux Pierre La Rocca, rapport ADEME IT4Green, Shift Project). Approche nuancée : l\'empreinte d\'un outil numérique est d\'un ordre de grandeur inférieur à celle du système agricole, mais attention aux effets de complexité et de saturation réseau.',
      result: 'Présentation équilibrée démontrant que gains agronomiques et innovations technologiques sont compatibles, sans techno-solutionnisme béat',
      image: '/placeholder-empreinte-numerique.jpg',
      tags: ['Conférence', 'Climat', 'Recherche', 'Numérique'],
      articleSlug: 'empreinte-numerique',
      articleImage: 'https://bnbshpvnzjuayggbyjqc.supabase.co/storage/v1/object/public/articles-images/og/empreinte-numerique.png',
      articleTitle: 'L\'empreinte environnementale du numérique et les transitions agricoles',
    },
    {
      client: 'Souveraineté et innovation technologique en agriculture',
      context: 'Agri Sud Ouest Innovation - Printemps 2025',
      challenge: 'Éclairer le débat sur l\'arbitrage entre souveraineté et compétitivité dans l\'innovation agricole, dans un contexte de tensions géopolitiques accrues',
      solution: 'Intervention lors d\'une table ronde aux côtés de Dominique Carlac\'h (DET Consultants) sur le thème "Guerre économique - Économie de guerre : quand la souveraineté prime sur la compétitivité, quel impact sur l\'innovation ?". Intégration des éléments du rapport Draghi sur l\'autonomie stratégique européenne.',
      result: 'Débat approfondi sur les implications géopolitiques pour le secteur agricole, interview post-événement pour prolonger la réflexion',
      image: '/placeholder-souverainete.jpg',
      tags: ['Table ronde', 'Souveraineté', 'Innovation', 'Géopolitique'],
      articleSlug: 'souverainete-et-innovation-technologique-en-agriculture',
      articleImage: 'https://bnbshpvnzjuayggbyjqc.supabase.co/storage/v1/object/public/articles-images/og/souverainete-et-innovation-technologique-en-agriculture.jpg',
      articleTitle: 'Souveraineté et innovation technologique en agriculture',
    },
    {
      client: 'Héritage et innovation',
      context: 'Salon International de l\'Agriculture 2025 - Paris',
      challenge: 'Intervenir au Salon de l\'Agriculture sur l\'articulation entre héritage agricole français et innovations technologiques, aux côtés d\'experts reconnus (Aikiko Suwa-Eisenman, Mauritz Quaak, Nicolas Chabanne)',
      solution: 'Présentation solo de 15 minutes analysant les systèmes agricoles français (dépendance fossiles, déspatialisation élevage/cultures, circuits longs) et les multiples formes d\'innovation (agronomique, organisationnelle, sociale, technologique). Cadrage analytique cas par cas pour évaluer impacts directs, indirects et systémiques des technologies (travaux The Shift Project).',
      result: 'Contribution reconnue lors d\'un événement grand public majeur, approche nuancée saluée par les participants',
      image: '/placeholder-sia.jpg',
      tags: ['Conférence', 'SIA', 'Héritage', 'Innovation'],
      articleSlug: 'technologies-agriculture-resiliente-prospere',
      articleImage: 'https://bnbshpvnzjuayggbyjqc.supabase.co/storage/v1/object/public/articles-images/og/technologies-agriculture-resiliente-prospere.png',
      articleTitle: 'Technologies pour une agriculture résiliente et prospère',
    },
    {
      client: 'Animation de tables rondes numérique agricole',
      context: 'PEPR AgroEcoNum - Rencontre chercheurs-professionnels 2025',
      challenge: 'Animer deux tables rondes d\'une journée entière sur le numérique agricole, facilitant les échanges entre chercheurs et professionnels du secteur',
      solution: 'Modération de 2 tables rondes successives lors de la rencontre annuelle du PEPR AgroEcoNum. Rôle de facilitateur pour créer un dialogue constructif entre monde académique et acteurs de terrain. Questions structurantes pour faire émerger enjeux scientifiques et opérationnels du numérique agroécologique.',
      result: 'Échanges riches et équilibrés entre chercheurs et professionnels, identification de pistes de collaboration, retours positifs sur la qualité de l\'animation',
      image: '/placeholder-pepr.jpg',
      tags: ['Animation', 'Table ronde', 'Recherche', 'AgroEcoNum'],
      articleSlug: 'agroecologie-numerique',
      articleImage: 'https://bnbshpvnzjuayggbyjqc.supabase.co/storage/v1/object/public/articles-images/og/agroecologie-numerique.png',
      articleTitle: 'Agroécologie numérique',
      externalLink: 'https://www.pepr-agroeconum.fr/agenda/rencontre-chercheurs-professionnels-2025',
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#FF9500] to-[#e08700] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Breadcrumb */}
            <div className="text-sm mb-6 opacity-90">
              <a href={`/${currentLocale}`} className="hover:underline">
                {t('breadcrumb.home')}
              </a>
              {' > '}
              <a href={`/${currentLocale}/prestations`} className="hover:underline">
                {t('breadcrumb.prestations')}
              </a>
              {' > '}
              <span>{t('breadcrumb.current')}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Texte */}
              <div>
                <h1 className="text-4xl md:text-6xl font-heading font-extrabold mb-6">
                  {t('hero.title')}
                </h1>
                <p className="text-xl mb-8 opacity-95">
                  {t('hero.subtitle')}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={`/${currentLocale}/contact`}
                    className="px-8 py-4 bg-white text-[#FF9500] rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg text-center"
                  >
                    {t('hero.ctaPrimary')}
                  </a>
                  <a
                    href="#cas-usage"
                    className="px-8 py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center"
                  >
                    {t('hero.ctaSecondary')}
                  </a>
                </div>
              </div>

              {/* Visuel */}
              <div className="bg-white/10 rounded-lg p-8 backdrop-blur-sm border border-white/20">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded p-4 text-center">
                    <Mic className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Conférences</p>
                  </div>
                  <div className="bg-white/10 rounded p-4 text-center">
                    <Users className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Tables rondes</p>
                  </div>
                  <div className="bg-white/10 rounded p-4 text-center">
                    <Radio className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Médias</p>
                  </div>
                  <div className="bg-white/10 rounded p-4 text-center">
                    <Users className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Animation débats / tables rondes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pour qui */}
      <ServiceTargetSection
        title="Pour qui ?"
        subtitle="Intervenant pédagogique pour vulgariser le numérique agricole auprès de tous les publics"
        audiences={targetAudiences}
        accentColor="orange"
      />

      {/* Formats */}
      <section className="py-12 bg-aspexit-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-heading font-extrabold text-aspexit-black mb-4 text-center">
              Formats d'intervention
            </h2>
            <p className="text-center text-aspexit-gray mb-16 max-w-2xl mx-auto">
              Des formats adaptés à vos événements et besoins de communication
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Conférences */}
              <div className="bg-gradient-to-br from-[#FF9500]/5 to-white rounded-2xl p-8 border-2 border-[#FF9500]/20 hover:border-[#FF9500]/40 hover:shadow-xl transition-all duration-300">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    {/* Micro sur pied */}
                    <rect x="46" y="60" width="8" height="30" fill="#FF9500" opacity="0.8"/>
                    <rect x="35" y="88" width="30" height="4" rx="2" fill="#FF9500"/>
                    {/* Tête de micro */}
                    <ellipse cx="50" cy="35" rx="12" ry="20" fill="#FF9500"/>
                    <ellipse cx="50" cy="35" rx="8" ry="16" fill="white" opacity="0.3"/>
                    {/* Ondes sonores */}
                    <path d="M 65 25 Q 72 25 72 35 Q 72 45 65 45" stroke="#FF9500" strokeWidth="2" fill="none" opacity="0.6"/>
                    <path d="M 75 20 Q 85 20 85 35 Q 85 50 75 50" stroke="#FF9500" strokeWidth="2" fill="none" opacity="0.4"/>
                    <path d="M 35 25 Q 28 25 28 35 Q 28 45 35 45" stroke="#FF9500" strokeWidth="2" fill="none" opacity="0.6"/>
                    <path d="M 25 20 Q 15 20 15 35 Q 15 50 25 50" stroke="#FF9500" strokeWidth="2" fill="none" opacity="0.4"/>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-aspexit-black mb-4 text-center">
                  Conférences
                </h3>
                <ul className="space-y-2 text-sm text-aspexit-gray">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#FF9500] flex-shrink-0 mt-0.5" />
                    <span>Durée : 30 à 90 minutes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#FF9500] flex-shrink-0 mt-0.5" />
                    <span>Public : 20 à 500 personnes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#FF9500] flex-shrink-0 mt-0.5" />
                    <span>Format : Présentation + Q&A</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#FF9500] flex-shrink-0 mt-0.5" />
                    <span>Support : Slides pédagogiques fournis</span>
                  </li>
                </ul>
              </div>

              {/* Tables rondes */}
              <div className="bg-gradient-to-br from-[#55BC8C]/5 to-white rounded-2xl p-8 border-2 border-[#55BC8C]/20 hover:border-[#55BC8C]/40 hover:shadow-xl transition-all duration-300">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    {/* Table ronde */}
                    <ellipse cx="50" cy="55" rx="35" ry="12" fill="#55BC8C" opacity="0.2"/>
                    <ellipse cx="50" cy="50" rx="35" ry="12" fill="#55BC8C" opacity="0.3"/>
                    {/* Personnes autour de la table */}
                    <circle cx="50" cy="20" r="6" fill="#55BC8C"/>
                    <circle cx="25" cy="40" r="6" fill="#55BC8C" opacity="0.8"/>
                    <circle cx="75" cy="40" r="6" fill="#55BC8C" opacity="0.8"/>
                    <circle cx="35" cy="65" r="6" fill="#55BC8C" opacity="0.7"/>
                    <circle cx="65" cy="65" r="6" fill="#55BC8C" opacity="0.7"/>
                    {/* Lignes de dialogue */}
                    <path d="M 50 26 L 50 44" stroke="#55BC8C" strokeWidth="1.5" strokeDasharray="2,2" opacity="0.5"/>
                    <path d="M 28 43 L 47 47" stroke="#55BC8C" strokeWidth="1.5" strokeDasharray="2,2" opacity="0.5"/>
                    <path d="M 72 43 L 53 47" stroke="#55BC8C" strokeWidth="1.5" strokeDasharray="2,2" opacity="0.5"/>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-aspexit-black mb-4 text-center">
                  Tables rondes & Débats
                </h3>
                <ul className="space-y-2 text-sm text-aspexit-gray">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#55BC8C] flex-shrink-0 mt-0.5" />
                    <span>Durée : 45 à 120 minutes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#55BC8C] flex-shrink-0 mt-0.5" />
                    <span>Rôle : Intervenant expert OU modérateur</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#55BC8C] flex-shrink-0 mt-0.5" />
                    <span>Public : Événements pro, salons</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#55BC8C] flex-shrink-0 mt-0.5" />
                    <span>Approche : Équilibre et nuance</span>
                  </li>
                </ul>
              </div>

              {/* Médias */}
              <div className="bg-gradient-to-br from-[#0066FF]/5 to-white rounded-2xl p-8 border-2 border-[#0066FF]/20 hover:border-[#0066FF]/40 hover:shadow-xl transition-all duration-300">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    {/* Micro de journaliste */}
                    <rect x="42" y="35" width="16" height="40" rx="8" fill="#0066FF"/>
                    <rect x="42" y="35" width="16" height="15" rx="8" fill="#0066FF" opacity="0.4"/>
                    {/* Logo média */}
                    <circle cx="50" cy="45" r="5" fill="white"/>
                    <text x="50" y="49" fontSize="8" fontWeight="bold" fill="#0066FF" textAnchor="middle" fontFamily="system-ui">M</text>
                    {/* Main tenant le micro */}
                    <path d="M 40 75 L 40 68 Q 40 65 43 65 L 57 65 Q 60 65 60 68 L 60 75" fill="#0066FF" opacity="0.3"/>
                    {/* Ondes */}
                    <circle cx="50" cy="42" r="18" stroke="#0066FF" strokeWidth="2" fill="none" opacity="0.2"/>
                    <circle cx="50" cy="42" r="25" stroke="#0066FF" strokeWidth="1.5" fill="none" opacity="0.1"/>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-aspexit-black mb-4 text-center">
                  Interventions médias
                </h3>
                <ul className="space-y-2 text-sm text-aspexit-gray">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span>Format : Interview presse, podcast, vidéo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span>Durée : 15 à 60 minutes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span>Support : Article, podcast, reportage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span>Expertise : Vulgarisation technique</span>
                  </li>
                </ul>
              </div>

              {/* Webinaires */}
              <div className="bg-gradient-to-br from-[#6C5CE7]/5 to-white rounded-2xl p-8 border-2 border-[#6C5CE7]/20 hover:border-[#6C5CE7]/40 hover:shadow-xl transition-all duration-300">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    {/* Écran d'ordinateur */}
                    <rect x="20" y="25" width="60" height="40" rx="3" fill="#6C5CE7" opacity="0.2"/>
                    <rect x="20" y="25" width="60" height="40" rx="3" stroke="#6C5CE7" strokeWidth="3" fill="none"/>
                    {/* Webcam */}
                    <circle cx="50" cy="28" r="2.5" fill="#6C5CE7"/>
                    {/* Base */}
                    <path d="M 45 65 L 40 75 L 60 75 L 55 65 Z" fill="#6C5CE7" opacity="0.3"/>
                    {/* Visages participants (visio) */}
                    <rect x="25" y="30" width="18" height="14" rx="2" fill="#6C5CE7" opacity="0.4"/>
                    <rect x="47" y="30" width="18" height="14" rx="2" fill="#6C5CE7" opacity="0.4"/>
                    <rect x="25" y="47" width="18" height="14" rx="2" fill="#6C5CE7" opacity="0.4"/>
                    <rect x="47" y="47" width="18" height="14" rx="2" fill="#6C5CE7" opacity="0.4"/>
                    {/* Icône présentation */}
                    <circle cx="72" cy="40" r="4" fill="#6C5CE7"/>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-aspexit-black mb-4 text-center">
                  Webinaires
                </h3>
                <ul className="space-y-2 text-sm text-aspexit-gray">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#6C5CE7] flex-shrink-0 mt-0.5" />
                    <span>Durée : 30 à 90 minutes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#6C5CE7] flex-shrink-0 mt-0.5" />
                    <span>Public : Coopératives, instituts, entreprises</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#6C5CE7] flex-shrink-0 mt-0.5" />
                    <span>Format : Présentation + démo + Q&A</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#6C5CE7] flex-shrink-0 mt-0.5" />
                    <span>Technique : Zoom, Teams, ou votre plateforme</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cas d'usage */}
      <section id="cas-usage" className="py-16 bg-aspexit-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-aspexit-black mb-4 text-center">
              Exemples de réalisations
            </h2>
            <p className="text-xl text-aspexit-gray text-center mb-16 max-w-2xl mx-auto">
              Quelques interventions passées sur le numérique agricole, l'IA et le climat
            </p>

            <div className="space-y-12">
              {useCases.map((useCase, index) => (
                <UseCaseCard key={index} useCase={useCase} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi moi */}
      <section className="py-12 bg-aspexit-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-heading font-extrabold text-aspexit-black mb-12 text-center">
              Pourquoi me solliciter
            </h2>

            <div className="bg-white rounded-lg p-8 border-2 border-border">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-aspexit-gray">
                <li className="flex items-center gap-2">
                  <span className="text-[#FF9500]">✓</span>
                  <span>
                    <strong>10+ ans d'expertise</strong> numérique agricole
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#FF9500]">✓</span>
                  <span>
                    <strong>60+ publications</strong> de vulgarisation
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#FF9500]">✓</span>
                  <span>
                    <strong>Double compétence</strong> : agronomie + data
                    science + pédagogie
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#FF9500]">✓</span>
                  <span>
                    <strong>Réseau professionnel</strong> : coopératives,
                    instituts, startups
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#FF9500]">✓</span>
                  <span>
                    <strong>Co-créateur Wiki Agri Tech</strong> : référence
                    francophone
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#FF9500]">✓</span>
                  <span>
                    <strong>Posture équilibrée</strong> : ni pro-tech aveugle,
                    ni anti-tech dogmatique
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-br from-[#FF9500] to-[#e08700] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-heading font-bold mb-4">
              Vous organisez un événement ?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Discutons de votre besoin d'intervention
            </p>

            <div className="bg-white/10 rounded-lg p-8 backdrop-blur-sm border border-white/20 mb-8">
              <h3 className="text-xl font-semibold mb-4">
                Précisez dans votre demande :
              </h3>
              <ul className="text-left space-y-2 max-w-md mx-auto">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>
                    Type d'événement (conférence, table ronde, média)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>Public cible (nombre, profil)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>Thématique souhaitée</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>Date et lieu</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>Format et durée</span>
                </li>
              </ul>
            </div>

            <div className="flex justify-center">
              <a
                href="/contact"
                className="px-8 py-4 bg-white text-[#FF9500] rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                Faire une demande
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSection />
    </div>
  );
}
